/*
 * "Select all 68 in this list", as Gmail offers it once a whole page is ticked: every page of the
 * list shown is read, through the list's own read (`readPage` in `use-inbox.ts`), and the rows are
 * held as the selection, so an action takes all of them. Up to `INBOX_BULK_LIMIT`, what one bulk
 * action takes; a longer list says so instead of offering it.
 *
 * The selection is of the list as it stood (Dawid, 2026-11-04, "Cleared on change"): any change to
 * the search, the filters, the place or the order drops it, and the toast says so
 * (`useSelection`). Unticking any row goes back to the page's ticks, less that row.
 */
import { useEffect, useRef, useState } from "react";
import { appCount } from "../../src/app-dates.ts";
import {
  INBOX_BULK_LIMIT,
  type InboxConversation,
  type InboxPage,
} from "../../src/inbox-v2/contract.ts";

/** Pages read at once: a 1,250 list is 25 reads, in seven rounds. */
const AT_ONCE = 4;

/**
 * Every row of the list, read page by page from its start, up to `INBOX_BULK_LIMIT`. A row that moved
 * between pages while they were read is held once; one that moved past a page already read is not
 * held, and the count says what is.
 */
export async function readWholeList(
  read: (offset: number) => Promise<InboxPage>,
  total: number,
  pageSize: number,
): Promise<InboxConversation[]> {
  const offsets: number[] = [];
  for (let offset = 0; offset < Math.min(total, INBOX_BULK_LIMIT); offset += pageSize)
    offsets.push(offset);
  const pages: InboxPage[] = [];
  for (let start = 0; start < offsets.length; start += AT_ONCE)
    pages.push(...(await Promise.all(offsets.slice(start, start + AT_ONCE).map(read))));
  const rows = new Map<string, InboxConversation>();
  for (const page of pages)
    for (const row of page.conversations) if (!rows.has(row.id)) rows.set(row.id, row);
  return [...rows.values()].slice(0, INBOX_BULK_LIMIT);
}

/** What the line under the bar is doing for this list: offering, reading, or failed to read. */
type Reading = { readonly key: string; readonly failed: boolean } | null;

/**
 * Reads the whole list on the rep's word and hands its rows to the selection. A list that changed
 * while it was read gets nothing: its rows would answer the list before.
 */
export function useSelectWhole(
  listKey: string,
  readPage: (offset: number) => Promise<InboxPage>,
  hold: (key: string, rows: readonly InboxConversation[]) => void,
) {
  const [reading, setReading] = useState<Reading>(null);
  // The list and the selection as they are when the read answers, not as they were when it began.
  const current = useRef({ listKey, hold, mounted: true });
  current.current = { ...current.current, listKey, hold };
  useEffect(() => {
    current.current.mounted = true;
    return () => {
      current.current.mounted = false;
    };
  }, []);
  const shown = reading?.key === listKey ? reading : null;
  const start = async (total: number, pageSize: number) => {
    const key = listKey;
    setReading({ key, failed: false });
    const still = () => current.current.mounted && current.current.listKey === key;
    try {
      const rows = await readWholeList(readPage, total, pageSize);
      if (!still()) return;
      setReading(null);
      current.current.hold(key, rows);
    } catch {
      if (still()) setReading({ key, failed: true });
    }
  };
  return { reading: shown !== null && !shown.failed, failed: shown?.failed === true, start };
}

/**
 * The line under the bar once every row on the page is ticked and the list holds more: the offer,
 * "Selecting all 68…" while it reads, a failure with Try again, or, past the limit, why not.
 */
export function WholeListOffer(props: {
  readonly total: number;
  readonly reading: boolean;
  readonly failed: boolean;
  readonly onSelect: () => void;
}) {
  const all = appCount(props.total);
  const body = () => {
    if (props.total > INBOX_BULK_LIMIT)
      return `Up to ${appCount(INBOX_BULK_LIMIT)} can be selected at once. Narrow the list to take the rest.`;
    if (props.reading) return `Selecting all ${all}…`;
    if (props.failed)
      return (
        <>
          Could not select all {all}.{" "}
          <button type="button" className="lcr-linklike" onClick={props.onSelect}>
            Try again
          </button>
        </>
      );
    return (
      <button type="button" className="lcr-linklike" onClick={props.onSelect}>
        Select all {all} in this list
      </button>
    );
  };
  return (
    <div className="lcr-bulk-whole" role="status">
      {body()}
    </div>
  );
}
